import { SignatureV4 } from '@smithy/signature-v4';
import { HttpRequest } from '@smithy/protocol-http';
import { Sha256 } from '@aws-crypto/sha256-js';
import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { AWSCredentials } from '@auth/types';

export interface SigV4BaseQueryArgs {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  path: string;
  params?: Record<string, string>;
  headers?: Record<string, string>;
  body?: unknown;
}

export interface SigV4BaseQueryError {
  status: number;
  message: string;
  data?: unknown;
}

// TODO: Add support for an unauthenticated base query for public routes.
// This would skip SigV4 signing and send requests directly to the API endpoint.
// See Requirement 13.1, 13.2 for planned public page API access.

/**
 * Factory function that creates an RTK Query base query with SigV4 request signing.
 *
 * Retrieves IAM credentials from the AuthProvider via the passed-in getter,
 * signs each request for the `execute-api` service, and handles 401/403
 * by refreshing credentials and retrying once.
 */
export function createSigV4BaseQuery(
  getCredentials: () => Promise<AWSCredentials>,
  apiEndpoint: string,
  region: string,
): BaseQueryFn<SigV4BaseQueryArgs, unknown, SigV4BaseQueryError> {
  return async (args) => {
    const { method, path, params, headers: extraHeaders, body } = args;

    const makeRequest = async (credentials: AWSCredentials): Promise<Response> => {
      const signer = new SignatureV4({
        service: 'execute-api',
        region,
        credentials: {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken,
        },
        sha256: Sha256,
      });

      const bodyStr = body != null ? JSON.stringify(body) : undefined;

      // Separate path and inline query string — RTK Query endpoints may
      // embed query params directly in the path (e.g. "/survey/templates?clinicId=x").
      // SigV4 requires the canonical URI and canonical query string to be
      // computed separately, so we split them here.
      const [pathOnly, inlineQs] = path.split('?');
      const mergedQuery: Record<string, string> = { ...params };
      if (inlineQs) {
        for (const part of inlineQs.split('&')) {
          const [k, v] = part.split('=');
          if (k) mergedQuery[decodeURIComponent(k)] = decodeURIComponent(v ?? '');
        }
      }

      const request = new HttpRequest({
        method,
        protocol: 'https:',
        hostname: apiEndpoint,
        path: pathOnly,
        headers: {
          host: apiEndpoint,
          ...(bodyStr ? { 'content-type': 'application/json' } : {}),
          ...extraHeaders,
        },
        ...(Object.keys(mergedQuery).length > 0 ? { query: mergedQuery } : {}),
        ...(bodyStr ? { body: bodyStr } : {}),
      });

      const signed = await signer.sign(request);

      const url = new URL(`https://${apiEndpoint}${pathOnly}`);
      for (const [key, value] of Object.entries(mergedQuery)) {
        url.searchParams.set(key, value);
      }

      // In dev mode with Vite proxy, fetch from the local dev server path
      // instead of the remote API endpoint. The SigV4 signature is still
      // computed against the real API hostname (in the signed headers).
      const isDev = typeof import.meta !== 'undefined' && import.meta.env?.DEV;
      const fetchUrl = isDev ? `${pathOnly}${url.search}` : url.toString();

      // Strip headers that browsers forbid or handle automatically.
      // The SigV4 signer includes 'host' for signature computation, but
      // browsers set Host automatically and reject it in fetch headers.
      const fetchHeaders = { ...signed.headers };
      if (!isDev) {
        delete fetchHeaders['host'];
      }

      return fetch(fetchUrl, {
        method,
        headers: fetchHeaders,
        body: bodyStr,
      });
    };

    try {
      let credentials = await getCredentials();
      let response = await makeRequest(credentials);

      // On 401/403: refresh credentials and retry once
      if (response.status === 401 || response.status === 403) {
        credentials = await getCredentials();
        response = await makeRequest(credentials);
      }

      if (!response.ok) {
        const errorBody = await response.text();
        let message: string;
        let data: unknown;
        try {
          const parsed = JSON.parse(errorBody);
          data = parsed;
          message = parsed.message ?? parsed.error ?? 'An unexpected error occurred.';
        } catch {
          message = 'An unexpected error occurred.';
        }
        return { error: { status: response.status, message, data } };
      }

      const data = await response.json();
      return { data };
    } catch {
      return {
        error: {
          status: 0,
          message: 'Unable to reach the server. Please check your connection and try again.',
        },
      };
    }
  };
}
